import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { normalizarTexto } from '@malgoos/shared';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, GripVertical, MoreHorizontal, Pencil, Plus, Settings2, Star, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Alternador, AreaSensivel, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { rotuloEtapaPipeline, rotuloFunilPipeline, rotuloMotivoPerda } from '../../nucleo/rotulos';
import { modulosWeb } from '../../nucleo/modulos-web';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { CLASSE_AREA } from './ConcluirAtividades';
import { EditorRico, htmlDoCampo } from '../../componentes/editor-rico';
import { CabecalhoConfig, CLASSE_TABELA_CONFIG, CLASSE_TD, CLASSE_TH, LinhaAlternador, ListaConfig } from './config-crm-base';
import { CAMINHO_CONFIGURACOES } from './crm-caminhos';
import { useListas, type Listas } from './crm-listas';
import { blocosDaPrevia, DocumentoProposta, useModelosProposta, type ModeloProposta } from './FichaPropostas';
import type { Questionario } from './questionario-modelo';
import type { Etapa, Funil, Motivo } from './tipos';

/**
 * CRM no novo desenho, lote 5 (C-F, ficha 2026-10-26 "crm-novo-desenho"): as seções das Configurações do CRM (as do clone do
 * RD) sobre o que o MalgoOS já tem. Lote crm7 (ficha 2026-10-29 "configuracoes-do-crm-igual-a-origem"): cada seção em página
 * inteira, com o cabeçalho e a forma de configurar da origem (funis em cartões com as etapas em linha, campos com abas por
 * entidade, fontes e campanhas em abas com a lista da origem, motivo de perda na mesma lista, qualificação em tabela com a
 * gaveta de editar, modelos de proposta em tabela com o editor em página). Os workflows estão em `Workflows.tsx`.
 */

const C = CAMINHO_CONFIGURACOES;
type EtapaConfig = Etapa & { playbook?: { objetivo?: string; descricao?: string; itens?: string[] } };

/** O botão "..." das linhas e cartões (o menu solto no body, para a tabela não cortar). */
const gatilhoMais = <MoreHorizontal className="size-5" aria-hidden />;
const CLASSE_GATILHO_MAIS = 'grid size-11 place-items-center rounded-lg text-tinta-2 hover:bg-sup-2 hover:text-tinta-1 md:size-8';

// ---------------------------------------------------------------------------------------------------------------- funis

export function FunisCrm() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeTudo = podeNoWorkspace(s, 'pipeline.configurar');
  const q = useQuery({ queryKey: ['pipeline', 'funis', 'todos'], queryFn: () => get<{ itens: Funil[] }>('/pipeline/funis', { arquivados: 'true' }) });
  const [novo, setNovo] = useState(false);
  const [renomear, setRenomear] = useState<Funil | null>(null);
  const [arquivar, setArquivar] = useState<Funil | null>(null);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline'] });
  const acao = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSuccess: async () => {
      toast.sucesso(tn('comum.salvo'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-funis="">
      <CabecalhoConfig
        titulo={t('crm.cfg.funis.titulo')}
        abas={[
          { chave: 'funil', rotulo: t('crm.cfg.funis.abaFunil'), para: `${C}/funis` },
          { chave: 'crm2crm', rotulo: t('crm.cfg.funis.abaCrm2crm') },
        ]}
        ativa="funil"
        intro={<p>{t('crm.configuracoes.funis.intro')}</p>}
      />
      <Estado consulta={q}>
        {(d) => {
          const ativos = d.itens.filter((f) => !f.arquivado_em);
          const arquivados = d.itens.filter((f) => f.arquivado_em);
          return (
            <>
              {ativos.map((f) => (
                <CartaoFunil key={f.id} funil={f} podeEditar={podeTudo} aoRenomear={() => setRenomear(f)} aoArquivar={() => setArquivar(f)} />
              ))}
              {podeTudo && (
                <Botao className="self-start" icone={<Plus className="size-4" />} onClick={() => setNovo(true)} data-adicionar-funil="">
                  {t('crm.cfg.funis.adicionarFunil')}
                </Botao>
              )}
              {arquivados.length > 0 && (
                <details className="rounded-xl border border-borda bg-sup-1" data-funis-arquivados="">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-tinta-2">{t('crm.cfg.funis.arquivados', { count: arquivados.length })}</summary>
                  <ul>
                    {arquivados.map((f) => (
                      <li key={f.id} className="flex flex-wrap items-center gap-3 border-t border-borda px-4 py-3 text-sm">
                        <span className="min-w-0 flex-1 text-tinta-2">{rotuloFunilPipeline(f.nome)}</span>
                        {podeTudo && (
                          <Botao tamanho="sm" onClick={() => acao.mutate(() => post(`/pipeline/funis/${f.id}/desarquivar`))}>
                            {t('crm.cfg.funis.desarquivar')}
                          </Botao>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {novo && <ModalNovoFunil ativos={ativos} aoFechar={() => setNovo(false)} aoSalvar={recarregar} />}
            </>
          );
        }}
      </Estado>
      {renomear && <ModalRenomearFunil funil={renomear} aoFechar={() => setRenomear(null)} aoSalvar={recarregar} />}
      <Confirmar
        aberto={!!arquivar}
        aoFechar={() => setArquivar(null)}
        perigoso
        titulo={t('crm.cfg.funis.confirmarArquivar', { nome: arquivar ? rotuloFunilPipeline(arquivar.nome) : '' })}
        texto={t('crm.cfg.funis.confirmarArquivarTexto')}
        aoConfirmar={() => {
          const f = arquivar;
          setArquivar(null);
          if (f) acao.mutate(() => post(`/pipeline/funis/${f.id}/arquivar`));
        }}
      />
    </section>
  );
}

/** crm6 item 4: a lista com o item de `de` levado para a posição `para` (função pura, testada em `tests/crm6-crm.test.ts`). */
export function moverNaLista<T>(lista: readonly T[], de: number, para: number): T[] {
  if (de === para || de < 0 || para < 0 || de >= lista.length || para >= lista.length) return [...lista];
  const r = [...lista];
  const [x] = r.splice(de, 1);
  r.splice(para, 0, x!);
  return r;
}

/** Um funil como na origem: o nome com o "..." e as etapas em linha (com rolagem), cada uma com "Configurar etapa", e o "+". */
function CartaoFunil({ funil, podeEditar, aoRenomear, aoArquivar }: { funil: Funil; podeEditar: boolean; aoRenomear: () => void; aoArquivar: () => void }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const nome = rotuloFunilPipeline(funil.nome);
  const q = useQuery({ queryKey: ['pipeline', 'etapas', funil.id], queryFn: () => get<{ itens: EtapaConfig[] }>('/pipeline/etapas', { funil_id: funil.id }) });
  const [editando, setEditando] = useState<EtapaConfig | 'nova' | null>(null);
  const [excluir, setExcluir] = useState<Etapa | null>(null);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline'] });
  // crm6 item 4: subir, descer ou arrastar a etapa grava a ordem do funil inteiro (a mesma do quadro); a ordem nova aparece na
  // hora e, se a gravação falhar, volta a do servidor.
  const [ordemLocal, setOrdemLocal] = useState<string[] | null>(null);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const reordenar = useMutation({
    mutationFn: (ids: string[]) => post('/pipeline/etapas/reordenar', { funil_id: funil.id, ids }),
    onMutate: (ids) => setOrdemLocal(ids),
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: async () => {
      await recarregar();
      setOrdemLocal(null);
    },
  });
  const apagar = useMutation({
    mutationFn: (e: Etapa) => del(`/pipeline/etapas/${e.id}`),
    onSuccess: async () => {
      setExcluir(null);
      setEditando(null);
      await recarregar();
    },
    onError: (e) => {
      setExcluir(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const itensMenu: ItemMenu[] = [{ chave: 'renomear', rotulo: t('crm.cfg.funis.renomear'), icone: <Pencil className="size-4" />, aoClicar: aoRenomear }];
  if (!funil.padrao) itensMenu.push({ chave: 'arquivar', rotulo: t('crm.cfg.funis.arquivar'), icone: <Trash2 className="size-4" />, perigo: true, separadorAntes: true, aoClicar: aoArquivar });
  return (
    <div className="min-w-0 rounded-xl border border-borda bg-sup-1" data-funil-config={nome}>
      <header className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-3">
        <h2 className="flex min-w-0 items-center gap-2 font-semibold">
          <span className="break-words">{nome}</span>
          {funil.padrao && <Selo>{t('funis.padrao')}</Selo>}
        </h2>
        {podeEditar && <Menu rotulo={t('crm.cfg.funis.opcoesFunil', { nome })} gatilho={gatilhoMais} classeGatilho={CLASSE_GATILHO_MAIS} itens={itensMenu} solto={{}} data-menu-funil={nome} />}
      </header>
      <Estado consulta={q}>
        {(d) => {
          const porId = new Map(d.itens.map((e) => [e.id, e]));
          const mesmas = ordemLocal && ordemLocal.length === d.itens.length && ordemLocal.every((id) => porId.has(id));
          const itens = mesmas ? ordemLocal.map((id) => porId.get(id)!) : d.itens;
          const ids = itens.map((e) => e.id);
          const mover = (de: number, para: number) => !reordenar.isPending && reordenar.mutate(moverNaLista(ids, de, para));
          return (
            <div className="overflow-x-auto p-4" role="region" tabIndex={0} aria-label={t('crm.cfg.funis.etapas', { nome })}>
              {/* Como na origem: as etapas numa linha do tempo (o círculo e o traço de cada uma) e o "+" no fim do traço. crm6 item 4:
                  a etapa vai para antes ou depois pelas setas ou arrastando. */}
              <ol className="flex w-max items-stretch" data-etapas-ordem="">
                {itens.map((e, i) => (
                  <li
                    key={e.id}
                    className={`flex w-56 shrink-0 flex-col gap-2 pr-4 text-sm ${arrastando === e.id ? 'opacity-60' : ''}`}
                    data-etapa-config={e.nome}
                    draggable={podeEditar}
                    onDragStart={(ev) => {
                      setArrastando(e.id);
                      ev.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => setArrastando(null)}
                    onDragOver={(ev) => arrastando && ev.preventDefault()}
                    onDrop={(ev) => {
                      ev.preventDefault();
                      const de = arrastando ? ids.indexOf(arrastando) : -1;
                      setArrastando(null);
                      if (de >= 0 && de !== i) mover(de, i);
                    }}
                  >
                    <span className="flex items-center" aria-hidden>
                      <span className={`size-5 shrink-0 rounded-full border-2 bg-sup-1 ${e.tipo === 'ganha' ? 'border-verde' : e.tipo === 'perdida' ? 'border-perigo' : 'border-acento-forte'}`} />
                      <span className="-mr-4 h-0.5 flex-1 bg-acento-forte" />
                    </span>
                    <span className="mt-1 flex items-start gap-1 font-semibold break-words">
                      {podeEditar && <GripVertical className="mt-0.5 size-4 shrink-0 cursor-grab text-tinta-3" aria-hidden />}
                      {/* Rlote-10 (VIS-M1): etapa de fábrica no idioma do app; o formulário de edição segue com o nome gravado. */}
                      <span className="min-w-0">{rotuloEtapaPipeline(e.nome)}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      <Selo cor={e.tipo === 'ganha' ? 'verde' : e.tipo === 'perdida' ? 'laranja' : 'neutro'}>{t(`etapaTipo.${e.tipo}`)}</Selo>
                      <span className="numero text-xs text-tinta-3">{t('crm.configuracoes.funis.probabilidade', { n: e.probabilidade })}</span>
                    </span>
                    {e.tipo === 'aberta' && (e.playbook?.objetivo || e.playbook?.descricao || e.playbook?.itens?.length) ? <Selo cor="verde">{t('crm.cfg.funis.etapaConfigurada')}</Selo> : null}
                    {podeEditar && (
                      <span className="flex flex-wrap items-center gap-1">
                        <Botao tamanho="sm" variante="fantasma" className="self-start !px-1 text-acento-forte" icone={<Settings2 className="size-4" />} onClick={() => setEditando(e)} data-configurar-etapa={e.nome}>
                          {t('crm.configuracoes.funis.configurarEtapa')}
                        </Botao>
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.configuracoes.funis.subirEtapa', { nome: rotuloEtapaPipeline(e.nome) })} title={t('crm.configuracoes.funis.subirEtapa', { nome: rotuloEtapaPipeline(e.nome) })} icone={<ChevronLeft className="size-4" />} disabled={i === 0} onClick={() => mover(i, i - 1)} data-subir-etapa={e.nome} />
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.configuracoes.funis.descerEtapa', { nome: rotuloEtapaPipeline(e.nome) })} title={t('crm.configuracoes.funis.descerEtapa', { nome: rotuloEtapaPipeline(e.nome) })} icone={<ChevronRight className="size-4" />} disabled={i === itens.length - 1} onClick={() => mover(i, i + 1)} data-descer-etapa={e.nome} />
                      </span>
                    )}
                  </li>
                ))}
                {podeEditar && (
                  <li className="flex shrink-0 items-start">
                    <Botao variante="primario" className="!size-11 !rounded-full !p-0 md:!size-8" aria-label={t('crm.cfg.funis.adicionarEtapa', { nome })} title={t('crm.cfg.funis.adicionarEtapa', { nome })} icone={<Plus className="size-4" />} onClick={() => setEditando('nova')} data-adicionar-etapa="" />
                  </li>
                )}
              </ol>
            </div>
          );
        }}
      </Estado>
      {editando && <FormEtapa funilId={funil.id} atual={editando === 'nova' ? null : editando} aoFechar={() => setEditando(null)} aoSalvar={recarregar} aoExcluir={editando !== 'nova' ? () => setExcluir(editando) : undefined} />}
      <Confirmar aberto={!!excluir} aoFechar={() => setExcluir(null)} perigoso carregando={apagar.isPending} titulo={t('crm.configuracoes.funis.confirmarExcluir', { nome: excluir?.nome ?? '' })} texto={t('crm.configuracoes.funis.confirmarExcluirTexto')} aoConfirmar={() => excluir && apagar.mutate(excluir)} />
    </div>
  );
}

/** Uma etapa do funil novo, antes de gravar: `nome` é o valor que vai ao servidor (o de fábrica fica intacto até renomear). */
export interface EtapaRascunho {
  chave: string;
  nome: string;
  /** Renomeada na janela: a entrada mostra o texto digitado; senão, o rótulo de fábrica no idioma do app. */
  editado: boolean;
  /** Nome copiado do funil de origem: redigitar o rótulo traduzido dele volta a gravar este nome (rcrm9 SPEC-B2). */
  original?: string;
  probabilidade?: number;
  tipo: Etapa['tipo'];
}

/** Etapa inicial quando não há de onde copiar (as três de fábrica, uma de cada tipo, como o servidor exige). */
const ETAPAS_INICIAIS: readonly Pick<EtapaRascunho, 'nome' | 'tipo'>[] = [
  { nome: 'Novo', tipo: 'aberta' },
  { nome: 'Ganho', tipo: 'ganha' },
  { nome: 'Perdido', tipo: 'perdida' },
];
const MAX_ETAPAS = 50;
const TIPOS_ETAPA = ['aberta', 'ganha', 'perdida'] as const;

const rascunhoDe = (e: Pick<EtapaRascunho, 'nome' | 'tipo'> & { probabilidade?: number }): EtapaRascunho => ({
  chave: crypto.randomUUID(),
  nome: e.nome,
  editado: false,
  original: e.nome,
  probabilidade: e.probabilidade,
  tipo: e.tipo,
});

/**
 * crm9 (crm6 item 4 na criação): erros da lista de etapas antes de gravar, com a mesma regra do servidor (`funis.criar`):
 * nome obrigatório e sem repetir no funil, ao menos uma etapa de cada tipo. Função pura, testada em `tests/crm9-criar-funil.test.ts`.
 */
export function errosDasEtapas(etapas: readonly EtapaRascunho[]): { porChave: Record<string, 'obrigatorio' | 'repetida'>; faltaTipo: Etapa['tipo'] | null } {
  const porChave: Record<string, 'obrigatorio' | 'repetida'> = {};
  const vistos = new Set<string>();
  // Mesma normalização do servidor (`normalizarTexto`: sem invisíveis nem espaços nas bordas) e, além do nome gravado, o
  // rótulo que aparece no idioma do app: "New" ao lado do "Novo" de fábrica (que aparece como "New") é repetida (rcrm9 B1/B2).
  for (const e of etapas) {
    const chave = normalizarTexto(e.nome).toLowerCase();
    const rotulo = normalizarTexto(rotuloEtapaPipeline(e.nome)).toLowerCase();
    if (!chave) porChave[e.chave] = 'obrigatorio';
    else if (vistos.has(chave) || vistos.has(rotulo)) porChave[e.chave] = 'repetida';
    vistos.add(chave);
    vistos.add(rotulo);
  }
  return { porChave, faltaTipo: TIPOS_ETAPA.find((t) => !etapas.some((e) => e.tipo === t)) ?? null };
}

/**
 * "Adicionar funil de vendas" em modal, como na origem (nome e as etapas iniciais). crm9 (crm6 item 4): as etapas vêm do
 * funil escolhido em "Copiar as etapas de" (o padrão, sem escolha) e se renomeiam, acrescentam, removem e reordenam (setas
 * ou arrastando, como no cartão do funil) antes de criar; o funil nasce com elas, na ordem da lista, numa chamada só
 * (`POST /pipeline/funis` com `etapas`).
 */
function ModalNovoFunil({ ativos, aoFechar, aoSalvar }: { ativos: Funil[]; aoFechar: () => void; aoSalvar: () => Promise<unknown> }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [nome, setNome] = useState('');
  const [copiarDe, setCopiarDe] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const origem = copiarDe || ativos.find((f) => f.padrao)?.id || '';
  const q = useQuery({
    queryKey: ['pipeline', 'etapas', origem],
    queryFn: () => get<{ itens: EtapaConfig[] }>('/pipeline/etapas', { funil_id: origem }),
    enabled: !!origem,
  });
  // A lista é da janela: nasce das etapas da origem e volta a elas quando a origem muda (troca de "Copiar as etapas de").
  const [etapas, setEtapas] = useState<{ origem: string; itens: EtapaRascunho[] } | null>(null);
  const [verErros, setVerErros] = useState(false);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [pegando, setPegando] = useState<string | null>(null);
  // Soltar o ponteiro fora da alça também solta a linha (rcrm9 ROB-B1): sem isso o nome dela seguiria arrastável.
  useEffect(() => {
    const soltar = () => setPegando(null);
    document.addEventListener('pointerup', soltar);
    document.addEventListener('pointercancel', soltar);
    return () => {
      document.removeEventListener('pointerup', soltar);
      document.removeEventListener('pointercancel', soltar);
    };
  }, []);
  const [foco, setFoco] = useState<{ chave: string; alvo: 'subir' | 'descer' | 'nome' } | null>(null);
  const listaRef = useRef<HTMLOListElement>(null);
  const daOrigem = q.data ? (q.data.itens.length ? q.data.itens : ETAPAS_INICIAIS).map(rascunhoDe) : null;
  if (etapas?.origem !== origem && (daOrigem || !origem)) setEtapas({ origem, itens: daOrigem ?? ETAPAS_INICIAIS.map(rascunhoDe) });
  const itens = etapas?.origem === origem ? etapas.itens : null;
  const mudar = (f: (l: EtapaRascunho[]) => EtapaRascunho[]) => setEtapas((x) => (x ? { ...x, itens: f(x.itens) } : x));
  const mover = (de: number, para: number, alvo?: 'subir' | 'descer') => {
    if (!itens) return;
    const chave = itens[de]?.chave;
    mudar((l) => moverNaLista(l, de, para));
    if (chave && alvo) setFoco({ chave, alvo });
  };
  // Teclado: depois de subir ou descer, o foco segue a etapa (no mesmo botão; no outro, se ele ficou desabilitado na ponta).
  useEffect(() => {
    if (!foco || !listaRef.current) return;
    const linha = [...listaRef.current.querySelectorAll<HTMLElement>('[data-etapa-nova]')].find((x) => x.dataset.etapaNova === foco.chave);
    const outro = foco.alvo === 'subir' ? 'descer' : 'subir';
    const el =
      foco.alvo === 'nome'
        ? linha?.querySelector<HTMLElement>('[data-nome-etapa-nova]')
        : (linha?.querySelector<HTMLButtonElement>(`[data-${foco.alvo}-etapa-nova]:not([disabled])`) ?? linha?.querySelector<HTMLElement>(`[data-${outro}-etapa-nova]`));
    el?.focus();
    setFoco(null);
  }, [foco]);
  const criar = useMutation({
    mutationFn: (lista: EtapaRascunho[]) =>
      post('/pipeline/funis', { nome: nome.trim(), etapas: lista.map((e) => ({ nome: e.nome.trim(), tipo: e.tipo, ...(e.probabilidade != null ? { probabilidade: e.probabilidade } : {}) })) }),
    onSuccess: async () => {
      toast.sucesso(tn('comum.salvo'));
      await aoSalvar();
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const validacao = itens ? errosDasEtapas(itens) : null;
  const salvar = () => {
    const semNome = !nome.trim();
    if (semNome) setErro(tn('erros.obrigatorio'));
    setVerErros(true);
    if (semNome || !itens || !validacao) return;
    const primeiro = itens.find((e) => validacao.porChave[e.chave]);
    if (primeiro) return setFoco({ chave: primeiro.chave, alvo: 'nome' });
    if (validacao.faltaTipo) return;
    criar.mutate(itens);
  };
  const nomeExibido = (e: EtapaRascunho, i: number) => (e.editado ? e.nome : rotuloEtapaPipeline(e.nome)) || t('crm.cfg.funis.etapaN', { n: i + 1 });
  const novaEtapa = () => {
    if (!itens || itens.length >= MAX_ETAPAS) return;
    // A etapa nova é aberta e entra antes da primeira de fechamento (ganha ou perdida), onde cabe no quadro.
    const nova: EtapaRascunho = { chave: crypto.randomUUID(), nome: '', editado: true, tipo: 'aberta' };
    const pos = itens.findIndex((e) => e.tipo !== 'aberta');
    mudar((l) => (pos < 0 ? [...l, nova] : [...l.slice(0, pos), nova, ...l.slice(pos)]));
    setFoco({ chave: nova.chave, alvo: 'nome' });
  };
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('crm.cfg.funis.adicionarFunil')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={criar.isPending} disabled={!itens} onClick={salvar} data-salvar-funil="">
            {t('acoes.adicionar')}
          </Botao>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        data-form-funil=""
        onSubmit={(e) => {
          e.preventDefault();
          salvar();
        }}
      >
        <Rotulo id={`${id}-nome`} rotulo={t('crm.cfg.funis.nomeFunil')} obrigatorio erro={erro ?? undefined}>
          <Entrada id={`${id}-nome`} autoFocus maxLength={60} placeholder={t('crm.cfg.funis.placeholderFunil')} value={nome} aria-invalid={!!erro} onChange={(e) => (setNome(e.target.value), setErro(null))} data-nome-funil="" />
        </Rotulo>
        <Rotulo id={`${id}-copia`} rotulo={t('crm.cfg.funis.copiarDe')}>
          <Selecao id={`${id}-copia`} vazio={t('crm.cfg.funis.semCopia')} opcoes={ativos.map((f) => ({ valor: f.id, rotulo: rotuloFunilPipeline(f.nome) }))} value={copiarDe} onChange={(e) => setCopiarDe(e.target.value)} data-copiar-etapas="" />
        </Rotulo>
        <fieldset className="flex min-w-0 flex-col gap-2" data-etapas-novo-funil="">
          <legend className="text-sm font-medium text-tinta-2">{t('crm.cfg.funis.etapasNovo')}</legend>
          <p className="text-xs text-tinta-3" id={`${id}-ajuda-etapas`}>
            {t('crm.cfg.funis.etapasNovoAjuda')}
          </p>
          {itens ? (
            <ol ref={listaRef} className="flex flex-col gap-2" aria-describedby={`${id}-ajuda-etapas`}>
              {itens.map((e, i) => {
                const rotulo = nomeExibido(e, i);
                const erroLinha = verErros ? validacao?.porChave[e.chave] : undefined;
                const doTipo = itens.filter((x) => x.tipo === e.tipo).length;
                return (
                  <li
                    key={e.chave}
                    className={`flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-borda bg-sup-1 p-2 ${arrastando === e.chave ? 'opacity-60' : ''}`}
                    data-etapa-nova={e.chave}
                    // Só a alça arrasta: com a linha inteira arrastável, selecionar o texto do nome com o mouse puxaria a etapa.
                    draggable={pegando === e.chave}
                    onDragStart={(ev) => {
                      setArrastando(e.chave);
                      ev.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => (setArrastando(null), setPegando(null))}
                    onDragOver={(ev) => arrastando && ev.preventDefault()}
                    onDrop={(ev) => {
                      ev.preventDefault();
                      const de = arrastando ? itens.findIndex((x) => x.chave === arrastando) : -1;
                      setArrastando(null);
                      if (de >= 0 && de !== i) mover(de, i);
                    }}
                  >
                    <span className="-m-1 flex cursor-grab self-stretch items-center p-1 text-tinta-3" aria-hidden onPointerDown={() => setPegando(e.chave)} onPointerUp={() => setPegando(null)} data-alca-etapa-nova="">
                      <GripVertical className="size-4 shrink-0" />
                    </span>
                    <span className={`size-4 shrink-0 rounded-full border-2 ${e.tipo === 'ganha' ? 'border-verde' : e.tipo === 'perdida' ? 'border-perigo' : 'border-acento-forte'}`} aria-hidden />
                    <span className="flex min-w-0 flex-1 basis-40 flex-col gap-1">
                      <Entrada
                        aria-label={t('crm.cfg.funis.nomeEtapaN', { n: i + 1 })}
                        aria-invalid={!!erroLinha}
                        maxLength={60}
                        value={e.editado ? e.nome : rotuloEtapaPipeline(e.nome)}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          // Redigitar o rótulo traduzido do nome de fábrica volta ao nome original (não grava a tradução).
                          mudar((l) => l.map((x) => (x.chave !== e.chave ? x : x.original && v.trim() === rotuloEtapaPipeline(x.original) ? { ...x, nome: x.original, editado: false } : { ...x, nome: v, editado: true })));
                        }}
                        data-nome-etapa-nova=""
                      />
                      {erroLinha && (
                        <span className="text-xs text-perigo" role="alert">
                          {erroLinha === 'obrigatorio' ? tn('erros.obrigatorio') : t('crm.cfg.funis.etapaRepetida')}
                        </span>
                      )}
                    </span>
                    <Selo cor={e.tipo === 'ganha' ? 'verde' : e.tipo === 'perdida' ? 'laranja' : 'neutro'}>{t(`etapaTipo.${e.tipo}`)}</Selo>
                    <span className="flex items-center gap-1">
                      <Botao tamanho="sm" variante="fantasma" className="!size-11 !p-0 md:!size-8" aria-label={t('crm.configuracoes.funis.subirEtapa', { nome: rotulo })} title={t('crm.configuracoes.funis.subirEtapa', { nome: rotulo })} icone={<ChevronUp className="size-4" />} disabled={i === 0} onClick={() => mover(i, i - 1, 'subir')} data-subir-etapa-nova="" />
                      <Botao tamanho="sm" variante="fantasma" className="!size-11 !p-0 md:!size-8" aria-label={t('crm.configuracoes.funis.descerEtapa', { nome: rotulo })} title={t('crm.configuracoes.funis.descerEtapa', { nome: rotulo })} icone={<ChevronDown className="size-4" />} disabled={i === itens.length - 1} onClick={() => mover(i, i + 1, 'descer')} data-descer-etapa-nova="" />
                      {/* A última etapa de um tipo não sai: o funil precisa de uma de cada (aberta, ganha, perdida). */}
                      <Botao tamanho="sm" variante="fantasma" className="!size-11 !p-0 md:!size-8" aria-label={t('crm.cfg.funis.removerEtapa', { nome: rotulo })} title={t('crm.cfg.funis.removerEtapa', { nome: rotulo })} icone={<Trash2 className="size-4" />} disabled={doTipo <= 1} onClick={() => mudar((l) => l.filter((x) => x.chave !== e.chave))} data-remover-etapa-nova="" />
                    </span>
                  </li>
                );
              })}
            </ol>
          ) : (
            <Estado consulta={q}>{() => null}</Estado>
          )}
          {verErros && validacao?.faltaTipo && (
            <p className="text-sm text-perigo" role="alert" data-falta-tipo="">
              {t('crm.cfg.funis.faltaTipo', { tipo: t(`etapaTipo.${validacao.faltaTipo}`) })}
            </p>
          )}
          {itens && itens.length < MAX_ETAPAS && (
            <Botao tamanho="sm" className="self-start" icone={<Plus className="size-4" />} onClick={novaEtapa} data-nova-etapa-funil="">
              {t('crm.cfg.funis.novaEtapa')}
            </Botao>
          )}
        </fieldset>
      </form>
    </Modal>
  );
}

function ModalRenomearFunil({ funil, aoFechar, aoSalvar }: { funil: Funil; aoFechar: () => void; aoSalvar: () => Promise<unknown> }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [nome, setNome] = useState(rotuloFunilPipeline(funil.nome));
  const [erro, setErro] = useState<string | null>(null);
  const salvar = useMutation({
    mutationFn: () => patch(`/pipeline/funis/${funil.id}`, { nome: nome.trim() }),
    onSuccess: async () => {
      toast.sucesso(tn('comum.salvo'));
      await aoSalvar();
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // O campo abre com o nome traduzido (funil de fábrica em en/es); salvar sem mudar não grava a tradução no lugar do nome.
  const ir = () => (!nome.trim() ? setErro(tn('erros.obrigatorio')) : nome.trim() === rotuloFunilPipeline(funil.nome) ? aoFechar() : salvar.mutate());
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.cfg.funis.renomearTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} onClick={ir} data-salvar-renomear-funil="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          ir();
        }}
      >
        <Rotulo id={`${id}-nome`} rotulo={t('crm.cfg.funis.nomeFunil')} obrigatorio erro={erro ?? undefined}>
          <Entrada id={`${id}-nome`} autoFocus maxLength={60} value={nome} aria-invalid={!!erro} onChange={(e) => (setNome(e.target.value), setErro(null))} data-renomear-funil="" />
        </Rotulo>
      </form>
    </Modal>
  );
}

function FormEtapa({ funilId, atual, aoFechar, aoSalvar, aoExcluir }: { funilId: string; atual: EtapaConfig | null; aoFechar: () => void; aoSalvar: () => Promise<unknown>; aoExcluir?: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [nome, setNome] = useState(atual?.nome ?? '');
  const [probabilidade, setProbabilidade] = useState(String(atual?.probabilidade ?? 10));
  const [objetivo, setObjetivo] = useState(atual?.playbook?.objetivo ?? '');
  const [descricao, setDescricao] = useState(atual?.playbook?.descricao ?? '');
  const [itens, setItens] = useState<string[]>(atual?.playbook?.itens ?? []);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const { ref } = useValidacaoLocal(erros);
  const aberta = !atual || atual.tipo === 'aberta';
  const salvar = async () => {
    const e: Record<string, string> = {};
    if (!nome.trim()) e.nome = t('crm.form.obrigatorio');
    const p = Number(probabilidade);
    if (!Number.isInteger(p) || p < 0 || p > 100) e.probabilidade = t('crm.configuracoes.funis.probabilidadeInvalida');
    setErros(e);
    if (Object.keys(e).length) return;
    setSalvando(true);
    try {
      let etapaId = atual?.id;
      if (atual) await patch(`/pipeline/etapas/${atual.id}`, { nome: nome.trim(), probabilidade: p });
      else {
        etapaId = crypto.randomUUID();
        await post('/pipeline/etapas', { id: etapaId, funil_id: funilId, nome: nome.trim(), probabilidade: p, tipo: 'aberta' });
      }
      if (aberta) await api('PUT', `/pipeline/etapas/${etapaId}/playbook`, { objetivo, descricao, itens: itens.map((x) => x.trim()).filter(Boolean) });
      toast.sucesso(t('crm.configuracoes.salvo'));
      await aoSalvar();
      aoFechar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={atual ? t('crm.configuracoes.funis.configurarEtapa') : t('crm.configuracoes.funis.adicionarEtapa')}
      rodape={
        <>
          {/* Lote crm7: como na origem, o "Excluir etapa" fica dentro da configuração da etapa. */}
          {atual && aoExcluir && (
            <Botao variante="perigo" className="mr-auto" icone={<Trash2 className="size-4" />} onClick={aoExcluir} data-excluir-etapa="">
              {t('crm.cfg.funis.excluirEtapa')}
            </Botao>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvando} onClick={() => void salvar()} data-salvar-etapa="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form ref={ref} noValidate className="flex flex-col gap-4" data-form-etapa="" onSubmit={(e) => e.preventDefault()}>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Rotulo id={`${id}-nome`} rotulo={t('crm.configuracoes.funis.nomeEtapa')} obrigatorio erro={erros.nome}>
              <Entrada id={`${id}-nome`} data-nome-etapa="" maxLength={60} value={nome} onChange={(e) => setNome(e.target.value)} />
            </Rotulo>
          </div>
          <Rotulo id={`${id}-prob`} rotulo={t('campos.probabilidade')} erro={erros.probabilidade}>
            <Entrada id={`${id}-prob`} inputMode="numeric" value={probabilidade} onChange={(e) => setProbabilidade(e.target.value)} />
          </Rotulo>
        </div>
        {aberta && (
          <>
            <h3 className="text-sm font-semibold">{t('crm.playbook.titulo')}</h3>
            <Rotulo id={`${id}-obj`} rotulo={t('crm.playbook.objetivo')}>
              <AreaSensivel id={`${id}-obj`} data-objetivo-etapa="" rows={2} maxLength={2000} className={CLASSE_AREA} value={objetivo} onChange={(e) => setObjetivo(e.target.value)} />
            </Rotulo>
            <Rotulo id={`${id}-desc`} rotulo={t('crm.playbook.descricao')}>
              <AreaSensivel id={`${id}-desc`} rows={3} maxLength={5000} className={CLASSE_AREA} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
            </Rotulo>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-tinta-2">{t('crm.configuracoes.funis.itensPlaybook')}</legend>
              {itens.map((x, i) => (
                <div key={i} className="flex items-center gap-2">
                  <EntradaSensivel mascara={null} livre aria-label={t('crm.configuracoes.funis.itemN', { n: i + 1 })} data-item-playbook={i} maxLength={300} value={x} onChange={(e) => setItens((l) => l.map((y, j) => (j === i ? e.target.value : y)))} />
                  <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.configuracoes.funis.removerItem', { n: i + 1 })} icone={<Trash2 className="size-4" />} onClick={() => setItens((l) => l.filter((_, j) => j !== i))} />
                </div>
              ))}
              {itens.length < 30 && (
                <Botao tamanho="sm" className="self-start" icone={<Plus className="size-4" />} onClick={() => setItens((l) => [...l, ''])} data-adicionar-item-playbook="">
                  {t('crm.configuracoes.funis.adicionarItem')}
                </Botao>
              )}
            </fieldset>
          </>
        )}
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------------------------------------------- campos

/**
 * As abas de "Configurar campos de cadastro" e a entidade de cada uma. Lista de 03/11, item 7: Negociação, Empresas, Contatos e
 * Produtos e Serviços. Empresa e contato são a mesma entidade no MalgoOS (`cadastros.parceiro`); a aba fixa a condição `tipo`
 * do campo (criar com ela, listar os dela e os sem condição, que valem nas duas). Produtos e Serviços é o catálogo do card da
 * negociação (`cadastros.servico`); o `estoque.produto` segue nas Configurações gerais.
 */
const ABAS_CAMPOS = [
  { chave: 'negociacao', entidade: 'pipeline.oportunidade', rotulo: 'abaNegociacao', caminho: '', tipo: null },
  { chave: 'empresa', entidade: 'cadastros.parceiro', rotulo: 'abaEmpresa', caminho: '/empresa', tipo: 'empresa' },
  { chave: 'contato', entidade: 'cadastros.parceiro', rotulo: 'abaContato', caminho: '/contato', tipo: 'pessoa' },
  { chave: 'produto', entidade: 'cadastros.servico', rotulo: 'abaProduto', caminho: '/produto', tipo: null },
] as const;

export function CamposCrm({ aba = 'negociacao' }: { aba?: string }) {
  // A lista e a gaveta são as do Configurações, publicadas no módulo (`camposPorEntidade`), sem importar a tela de lá.
  const Campos = modulosWeb.find((m) => m.camposPorEntidade)?.camposPorEntidade;
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const podeVer = s.pode('configuracoes.ver');
  const entidades = useQuery({ queryKey: ['configuracoes', 'campos', 'entidades'], queryFn: () => get<{ entidade: string; modulo: string }[]>('/configuracoes/campos/entidades'), enabled: podeVer });
  const disponiveis = ABAS_CAMPOS.filter((a) => entidades.data?.some((e) => e.entidade === a.entidade));
  // A aba `produto` do endereço antigo (`estoque.produto`) agora é o catálogo de serviços: o caminho segue valendo.
  const atual = disponiveis.find((a) => a.chave === aba) ?? disponiveis[0];
  const cabecalho = (novo: ReactNode) => (
    <CabecalhoConfig
      titulo={t('crm.cfg.campos.titulo')}
      acao={novo}
      abas={disponiveis.map((a) => ({ chave: a.chave, rotulo: t(`crm.cfg.campos.${a.rotulo}`), para: `${C}/campos${a.caminho}` }))}
      ativa={atual?.chave}
    />
  );
  if (!podeVer)
    return (
      <section className="flex min-w-0 flex-col gap-5" data-config-campos="">
        {cabecalho(null)}
        <p className="rounded-xl border border-borda bg-sup-1 p-4 text-sm text-tinta-2">{t('crm.cfg.campos.semPermissao')}</p>
      </section>
    );
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-campos="" data-aba-campos={atual?.chave ?? ''}>
      <Estado consulta={entidades}>
        {() =>
          atual && Campos ? (
            <Campos key={atual.chave} entidadeFixa={atual.entidade} cabecalho={cabecalho} condicaoAba={atual.tipo ? { campo: 'tipo', valor: atual.tipo, aba: t(`crm.cfg.campos.${atual.rotulo}`) } : undefined} />
          ) : (
            cabecalho(null)
          )
        }
      </Estado>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------- fontes e campanhas

/** Fontes e campanhas em duas abas, como na origem: o alternador "Permitir que usuários criem..." e a lista da origem. */
export function FontesCampanhas({ tipo = 'fontes' }: { tipo?: 'fontes' | 'campanhas' }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useListas();
  // Val5: fontes e campanhas são configuração do CRM (`pipeline.configurar`, o mesmo do PUT das listas).
  const podeEditar = s.pode('pipeline.configurar');
  const gravar = async (corpo: Partial<Listas>) => {
    try {
      await api('PUT', '/pipeline/crm/listas', corpo);
      toast.sucesso(t('crm.configuracoes.salvo'));
      await qc.invalidateQueries({ queryKey: ['pipeline', 'crm', 'listas'] });
    } catch (e) {
      toast.erro(mensagemDeErro(e));
      throw e;
    }
  };
  const S = tipo === 'fontes' ? 'Fontes' : 'Campanhas';
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-fontes={tipo}>
      <CabecalhoConfig
        titulo={t(`crm.cfg.fontes.aba${S}`)}
        abas={[
          { chave: 'fontes', rotulo: t('crm.cfg.fontes.abaFontes'), para: `${C}/fontes` },
          { chave: 'campanhas', rotulo: t('crm.cfg.fontes.abaCampanhas'), para: `${C}/fontes/campanhas` },
        ]}
        ativa={tipo}
        intro={<p>{t(`crm.cfg.fontes.intro${S}`)}</p>}
      />
      <Estado consulta={q}>
        {(d) => {
          const itens = d[tipo];
          const livres = tipo === 'fontes' ? d.fontes_livres : d.campanhas_livres;
          return (
            <>
              <LinhaAlternador
                texto={t(`crm.cfg.fontes.livres${S}`)}
                marcado={livres}
                desabilitado={!podeEditar}
                aoMudar={(v) => void gravar(tipo === 'fontes' ? { fontes_livres: v } : { campanhas_livres: v }).catch(() => undefined)}
                dado={`livres-${tipo}`}
              />
              <ListaConfig
                key={tipo}
                dado={tipo}
                itens={itens.map((x) => ({ chave: x.nome, nome: x.nome, descricao: x.descricao }))}
                comDescricao={tipo === 'campanhas'}
                podeCriar={podeEditar}
                podeExcluir={podeEditar}
                textos={{
                  adicionar: t(`crm.cfg.fontes.adicionar${S}`),
                  campo: t(`crm.cfg.fontes.campo${S}`),
                  placeholder: t(`crm.cfg.fontes.placeholder${S}`),
                  coluna: t(`crm.cfg.fontes.campo${S}`),
                  apagarSelecionados: (n) => t(`crm.cfg.fontes.apagarSel${S}`, { count: n }),
                  apagarTodos: (n) => t(`crm.cfg.fontes.apagarTodas${S}`, { count: n }),
                  confirmarApagar: (n) => t(`crm.cfg.fontes.confirmar${S}`, { count: n }),
                }}
                aoCriar={(v) => gravar({ [tipo]: [...itens, { nome: v.nome, descricao: v.descricao }] })}
                aoExcluir={(chaves) => gravar({ [tipo]: itens.filter((x) => !chaves.includes(x.nome)) })}
              />
              <p className="text-xs text-tinta-3">{t('crm.configuracoes.fontes.ajuda')}</p>
            </>
          );
        }}
      </Estado>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------- motivo de perda

/**
 * Motivo de perda na lista da origem. Ficam do MalgoOS o renomear na linha (o lápis), a contagem de negociações perdidas e a
 * lixeira de restaurar; criar pede `pipeline.configurar` (val5), excluir `pipeline.excluir`.
 */
export function MotivosPerdaCrm() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['pipeline', 'motivos'], queryFn: () => get<{ itens: Motivo[] }>('/pipeline/motivos-perda') });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'motivos'] });
  const rodar = async (f: () => Promise<unknown>, aviso = tn('comum.salvo')) => {
    try {
      await f();
      toast.sucesso(aviso);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
      throw e;
    } finally {
      await recarregar();
    }
  };
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-motivos="">
      <CabecalhoConfig
        titulo={t('crm.cfg.motivos.titulo')}
        acao={s.pode('pipeline.excluir') ? <BotaoLixeira entidades={['pipeline.motivo_perda']} /> : undefined}
        intro={
          <>
            <p>{t('crm.cfg.motivos.intro1')}</p>
            <p>{t('crm.cfg.motivos.intro2')}</p>
          </>
        }
      />
      <Estado consulta={q}>
        {(d) => (
          <ListaConfig
            dado="motivos"
            itens={d.itens.map((m) => ({ chave: m.id, nome: rotuloMotivoPerda(m.nome) }))}
            podeCriar={s.pode('pipeline.configurar')}
            podeExcluir={s.pode('pipeline.excluir')}
            textos={{
              adicionar: t('crm.cfg.motivos.adicionar'),
              campo: t('crm.cfg.motivos.campo'),
              placeholder: t('crm.cfg.motivos.placeholder'),
              coluna: t('crm.cfg.motivos.coluna'),
              apagarSelecionados: (n) => t('crm.cfg.motivos.apagarSel', { count: n }),
              apagarTodos: (n) => t('crm.cfg.motivos.apagarTodos', { count: n }),
              confirmarApagar: (n) => t('crm.cfg.motivos.confirmar', { count: n }),
            }}
            colunasExtras={[{ rotulo: t('crm.cfg.motivos.perdidas'), celula: (i) => <span className="numero text-tinta-3">{d.itens.find((m) => m.id === i.chave)?.perdidas ?? 0}</span> }]}
            aoCriar={(v) => rodar(() => post('/pipeline/motivos-perda', { nome: v.nome }))}
            aoRenomear={(id, nome) => rodar(() => patch(`/pipeline/motivos-perda/${id}`, { nome }))}
            aoExcluir={(ids) =>
              rodar(async () => {
                for (const id of ids) await del(`/pipeline/motivos-perda/${id}`);
              }, tn('comum.removido'))
            }
          />
        )}
      </Estado>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------- qualificação

/**
 * Qualificação como na origem: o cartão da qualificação automática (no MalgoOS as faixas de pontos ficam em cada questionário,
 * então o cartão conta quantos qualificam e leva a eles) e a tabela dos 5 níveis, com o lápis abrindo a gaveta de editar.
 */
export function QualificacaoCrm() {
  const { t } = useTranslation('pipeline');
  const q = useListas();
  const quest = useQuery({ queryKey: ['pipeline', 'questionarios'], queryFn: () => get<{ itens: Questionario[] }>('/pipeline/questionarios') });
  const [editando, setEditando] = useState<number | null>(null);
  const qualificam = (quest.data?.itens ?? []).filter((x) => x.ativo && x.qualificar_auto).length;
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-qualificacao="">
      <CabecalhoConfig titulo={t('crm.configuracoes.secoes.qualificacao')} intro={<p>{t('crm.cfg.qualificacao.sub')}</p>} />
      <div className="flex flex-col gap-1 rounded-xl border border-borda bg-sup-1 p-4 text-sm" data-qualificacao-auto={qualificam}>
        <p className="font-semibold">{t('crm.cfg.qualificacao.autoTitulo')}</p>
        <p className="text-tinta-2">
          {qualificam ? t('crm.cfg.qualificacao.autoTexto', { count: qualificam }) : t('crm.cfg.qualificacao.autoNenhum')}{' '}
          <Link to={`${C}/questionarios`} className="font-medium text-acento-forte hover:underline">
            {t('crm.cfg.qualificacao.autoLink')}
          </Link>
        </p>
      </div>
      <Estado consulta={q}>
        {(d) => (
          <>
            <div className={`${CLASSE_TABELA_CONFIG} rounded-xl border border-borda bg-sup-1`} role="region" tabIndex={0} aria-label={t('crm.configuracoes.secoes.qualificacao')}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda">
                    <th className={CLASSE_TH}>{t('crm.cfg.qualificacao.colQualificacao')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.qualificacao.colNome')}</th>
                    <th className={`${CLASSE_TH} text-right`}>
                      <span className="sr-only">{t('crm.cfg.lista.opcoes')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.niveis.map((n, i) => (
                    <tr key={i} className="border-b border-borda last:border-b-0" data-nivel-config={i + 1}>
                      <td className={CLASSE_TD}>
                        <span className="flex items-center gap-0.5 text-dourado" aria-label={t('crm.comercial.estrelas', { count: i + 1 })} role="img">
                          {[1, 2, 3, 4, 5].map((k) => (
                            <Star key={k} className={`size-4 ${k <= i + 1 ? 'fill-current' : 'text-tinta-3 opacity-40'}`} aria-hidden />
                          ))}
                        </span>
                      </td>
                      <td className={`${CLASSE_TD} font-medium`}>{n}</td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.cfg.qualificacao.editar', { nome: n })} title={t('crm.cfg.qualificacao.editar', { nome: n })} icone={<Pencil className="size-4" />} onClick={() => setEditando(i)} data-editar-nivel={i + 1} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-tinta-3">{t('crm.configuracoes.qualificacao.ajuda')}</p>
            {editando !== null && <FormNivel niveis={d.niveis} indice={editando} aoFechar={() => setEditando(null)} />}
          </>
        )}
      </Estado>
    </section>
  );
}

function FormNivel({ niveis, indice, aoFechar }: { niveis: string[]; indice: number; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [nome, setNome] = useState(niveis[indice] ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const gravar = useMutation({
    mutationFn: () => api('PUT', '/pipeline/crm/listas', { niveis: niveis.map((x, j) => (j === indice ? nome.trim() : x.trim())) }),
    onSuccess: async () => {
      toast.sucesso(t('crm.configuracoes.salvo'));
      await qc.invalidateQueries({ queryKey: ['pipeline', 'crm', 'listas'] });
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const salvar = () => (nome.trim() ? gravar.mutate() : setErro(t('crm.configuracoes.qualificacao.semNome')));
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.cfg.qualificacao.editarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={gravar.isPending} onClick={salvar} data-salvar-nivel="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          salvar();
        }}
      >
        <span className="flex items-center gap-0.5 text-dourado" aria-label={t('crm.comercial.estrelas', { count: indice + 1 })} role="img">
          {[1, 2, 3, 4, 5].map((k) => (
            <Star key={k} className={`size-5 ${k <= indice + 1 ? 'fill-current' : 'text-tinta-3 opacity-40'}`} aria-hidden />
          ))}
        </span>
        <Rotulo id={`${id}-nome`} rotulo={t('crm.cfg.qualificacao.nome')} obrigatorio erro={erro ?? undefined}>
          <Entrada id={`${id}-nome`} autoFocus maxLength={40} value={nome} aria-invalid={!!erro} onChange={(e) => (setNome(e.target.value), setErro(null))} data-nome-nivel="" />
        </Rotulo>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------------------------------------------- questionários

// CRM lote 4 (28/10, N1): o questionário no estilo do Google Formulários (editor em cartões e respostas coletadas).
export { QuestionariosCrm } from './Questionarios';

// ---------------------------------------------------------------------------------------------------------------- modelos de proposta

type FiltroModelos = 'todos' | 'publicados' | 'rascunhos';

/** Modelos de proposta como na origem: "Criar modelo" no cabeçalho, o filtro, a tabela com o status e o menu "...". */
export function ModelosPropostaCrm() {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const q = useModelosProposta();
  const [filtro, setFiltro] = useState<FiltroModelos>('todos');
  const [excluir, setExcluir] = useState<ModeloProposta | null>(null);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'proposta-modelos'] });
  const acao = useMutation({
    mutationFn: async ({ f, aviso }: { f: () => Promise<unknown>; aviso?: string }) => {
      await f();
      if (aviso) toast.sucesso(aviso);
    },
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const editar = (m: ModeloProposta) => navegar(`${C}/modelos/${m.id}`);
  const duplicar = (m: ModeloProposta) =>
    acao.mutate({
      aviso: t('crm.cfg.modelos.duplicado'),
      f: () =>
        post('/pipeline/proposta-modelos', {
          id: crypto.randomUUID(),
          nome: t('crm.cfg.modelos.copia', { nome: m.nome }).slice(0, 120),
          cor: m.cor || undefined,
          titulo: m.titulo,
          apresentacao_html: htmlDoCampo(m.apresentacao_html, m.apresentacao),
          condicoes_html: htmlDoCampo(m.condicoes_html, m.condicoes),
          rodape_html: htmlDoCampo(m.rodape_html, m.rodape),
          publicado: false,
        }),
    });
  const itensMenu = (m: ModeloProposta): ItemMenu[] => {
    const l: ItemMenu[] = [{ chave: 'editar', rotulo: t('crm.cfg.modelos.editar'), icone: <Pencil className="size-4" />, aoClicar: () => editar(m) }];
    if (!m.padrao) l.push({ chave: 'padrao', rotulo: t('crm.cfg.modelos.tornarPadrao'), aoClicar: () => acao.mutate({ f: () => patch(`/pipeline/proposta-modelos/${m.id}`, { padrao: true, publicado: true }), aviso: t('crm.configuracoes.salvo') }) });
    l.push({ chave: 'duplicar', rotulo: t('crm.cfg.modelos.duplicar'), aoClicar: () => duplicar(m) });
    if (!m.padrao) l.push({ chave: 'publicar', rotulo: m.publicado ? t('crm.cfg.modelos.moverRascunho') : t('crm.cfg.modelos.publicar'), aoClicar: () => acao.mutate({ f: () => patch(`/pipeline/proposta-modelos/${m.id}`, { publicado: !m.publicado }), aviso: t('crm.configuracoes.salvo') }) });
    if (!m.padrao) l.push({ chave: 'excluir', rotulo: t('crm.cfg.modelos.excluir'), icone: <Trash2 className="size-4" />, perigo: true, separadorAntes: true, aoClicar: () => setExcluir(m) });
    return l;
  };
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-modelos="">
      <CabecalhoConfig
        titulo={t('crm.configuracoes.secoes.modelos')}
        intro={<p>{t('crm.cfg.modelos.sub')}</p>}
        acao={
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => navegar(`${C}/modelos/novo`)} data-criar-modelo="">
            {t('crm.cfg.modelos.criar')}
          </Botao>
        }
      />
      <div className="w-full sm:w-64">
        <Selecao
          aria-label={t('crm.cfg.modelos.filtro')}
          value={filtro}
          onChange={(e) => setFiltro(e.target.value as FiltroModelos)}
          opcoes={[
            { valor: 'todos', rotulo: t('crm.cfg.modelos.todos') },
            { valor: 'publicados', rotulo: t('crm.cfg.modelos.publicados') },
            { valor: 'rascunhos', rotulo: t('crm.cfg.modelos.rascunhos') },
          ]}
          data-filtro-modelos=""
        />
      </div>
      <Estado consulta={q}>
        {(d) => {
          const lista = d.itens.filter((m) => filtro === 'todos' || (filtro === 'publicados' ? m.publicado : !m.publicado));
          return (
            <div className={`${CLASSE_TABELA_CONFIG} rounded-xl border border-borda bg-sup-1`} role="region" tabIndex={0} aria-label={t('crm.cfg.modelos.colModelo')}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda">
                    <th className={CLASSE_TH}>{t('crm.cfg.modelos.colModelo')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.modelos.colStatus')}</th>
                    <th className={`${CLASSE_TH} text-right`}>{t('crm.cfg.modelos.colAcoes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-tinta-3">
                        {t('crm.cfg.modelos.vazio')}
                      </td>
                    </tr>
                  )}
                  {lista.map((m) => (
                    <tr key={m.id} className="border-b border-borda last:border-b-0" data-modelo-config={m.nome}>
                      <td className={CLASSE_TD}>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="size-3 shrink-0 rounded-full border border-borda" style={{ background: m.cor }} aria-hidden />
                          <Link to={`${C}/modelos/${m.id}`} className="font-medium text-acento-forte hover:underline">
                            {m.nome}
                          </Link>
                          {m.padrao && <Selo>{t('funis.padrao')}</Selo>}
                        </span>
                      </td>
                      <td className={CLASSE_TD}>{m.publicado ? <Selo cor="verde">{t('crm.cfg.modelos.publicado')}</Selo> : <Selo>{t('crm.cfg.modelos.rascunho')}</Selo>}</td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <span className="inline-flex">
                          <Menu rotulo={t('crm.cfg.modelos.opcoes', { nome: m.nome })} gatilho={gatilhoMais} classeGatilho={CLASSE_GATILHO_MAIS} itens={itensMenu(m)} solto={{ direita: true }} data-menu-modelo={m.nome} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
      </Estado>
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        perigoso
        titulo={t('crm.configuracoes.modelos.confirmarExcluir', { nome: excluir?.nome ?? '' })}
        texto={t('crm.configuracoes.modelos.confirmarExcluirTexto')}
        aoConfirmar={() => {
          const m = excluir;
          setExcluir(null);
          if (m) acao.mutate({ f: () => del(`/pipeline/proposta-modelos/${m.id}`) });
        }}
      />
    </section>
  );
}

/** O editor do modelo em página (`modelos/:id` e `modelos/novo`), como na origem: definições à esquerda e o conteúdo à direita. */
export function EditorModeloProposta() {
  const { t } = useTranslation('pipeline');
  const { id = 'novo' } = useParams();
  const q = useModelosProposta();
  return (
    <Estado consulta={q}>
      {(d) => {
        const atual = id === 'novo' ? null : (d.itens.find((m) => m.id === id) ?? undefined);
        if (atual === undefined)
          return (
            <section className="flex flex-col gap-5">
              <CabecalhoConfig titulo={t('crm.configuracoes.secoes.modelos')} voltar={`${C}/modelos`} />
              <Vazio texto={t('crm.cfg.modelos.naoEncontrado')} />
            </section>
          );
        return <FormModelo key={atual?.id ?? 'novo'} atual={atual} corPadrao={d.itens.find((m) => m.padrao)?.cor ?? d.itens[0]?.cor} />;
      }}
    </Estado>
  );
}

function FormModelo({ atual, corPadrao }: { atual: ModeloProposta | null; corPadrao?: string }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const id = useId();
  // Lote crm4 (L9): os três blocos no editor com formatação (o modelo de antes do editor entra em parágrafos).
  const [m, setM] = useState({
    nome: atual?.nome ?? '',
    cor: atual?.cor ?? corPadrao ?? '',
    titulo: atual?.titulo ?? '',
    apresentacao: htmlDoCampo(atual?.apresentacao_html, atual?.apresentacao ?? ''),
    condicoes: htmlDoCampo(atual?.condicoes_html, atual?.condicoes ?? ''),
    rodape: htmlDoCampo(atual?.rodape_html, atual?.rodape ?? ''),
    publicado: atual?.publicado ?? true,
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const { ref } = useValidacaoLocal(erro ? { nome: erro } : null);
  const mudar = (d: Partial<typeof m>) => setM((x) => ({ ...x, ...d }));
  const salvar = async () => {
    if (!m.nome.trim()) return setErro(t('crm.form.obrigatorio'));
    setSalvando(true);
    try {
      const corpo = { nome: m.nome.trim(), cor: m.cor || undefined, titulo: m.titulo, apresentacao_html: m.apresentacao, condicoes_html: m.condicoes, rodape_html: m.rodape, publicado: m.publicado };
      const novoId = atual ? atual.id : crypto.randomUUID();
      if (atual) await patch(`/pipeline/proposta-modelos/${atual.id}`, corpo);
      else await post('/pipeline/proposta-modelos', { id: novoId, ...corpo });
      toast.sucesso(t('crm.cfg.modelos.salvo'));
      await qc.invalidateQueries({ queryKey: ['pipeline', 'proposta-modelos'] });
      if (!atual) navegar(`${C}/modelos/${novoId}`, { replace: true });
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <section className="flex min-w-0 flex-col gap-5" data-editor-modelo={atual?.id ?? 'novo'}>
      <CabecalhoConfig
        migalha={t('crm.configuracoes.secoes.modelos')}
        voltar={`${C}/modelos`}
        titulo={m.nome.trim() || atual?.nome || t('crm.cfg.modelos.novoTitulo')}
        acao={
          <Botao variante="primario" carregando={salvando} onClick={() => void salvar()} data-salvar-modelo="">
            {t('crm.cfg.modelos.salvarAlteracoes')}
          </Botao>
        }
        intro={
          <p className="flex flex-wrap items-center gap-2">
            {m.publicado ? <Selo cor="verde">{t('crm.cfg.modelos.publicado')}</Selo> : <Selo>{t('crm.cfg.modelos.rascunho')}</Selo>}
            {atual?.padrao && <Selo>{t('funis.padrao')}</Selo>}
          </p>
        }
      />
      <form ref={ref} noValidate className="flex min-w-0 flex-col gap-5 lg:flex-row lg:items-start" onSubmit={(e) => e.preventDefault()} data-form-modelo="">
        <aside className="flex flex-col gap-4 rounded-xl border border-borda bg-sup-1 p-4 lg:w-72 lg:shrink-0" aria-labelledby={`${id}-def`}>
          <h2 id={`${id}-def`} className="text-sm font-semibold">
            {t('crm.cfg.modelos.definicoes')}
          </h2>
          <Rotulo id={`${id}-nome`} rotulo={t('crm.configuracoes.modelos.nome')} obrigatorio erro={erro ?? undefined}>
            <Entrada id={`${id}-nome`} data-nome-modelo="" maxLength={120} value={m.nome} onChange={(e) => (mudar({ nome: e.target.value }), setErro(null))} />
          </Rotulo>
          <Rotulo id={`${id}-cor`} rotulo={t('crm.configuracoes.modelos.cor')}>
            <Entrada id={`${id}-cor`} type="color" value={m.cor || undefined} onChange={(e) => mudar({ cor: e.target.value })} />
          </Rotulo>
          <Rotulo id={`${id}-titulo`} rotulo={t('crm.proposta.campos.titulo')}>
            <EntradaSensivel mascara={null} livre id={`${id}-titulo`} maxLength={200} value={m.titulo} onChange={(e) => mudar({ titulo: e.target.value })} />
          </Rotulo>
          {!atual?.padrao && (
            <label className="flex items-center gap-3 text-sm">
              <Alternador marcado={m.publicado} rotulo={t('crm.configuracoes.modelos.publicado')} aoMudar={(v) => mudar({ publicado: v })} />
              {t('crm.configuracoes.modelos.publicado')}
            </label>
          )}
        </aside>
        <div className="flex min-w-0 flex-1 flex-col gap-4 rounded-xl border border-borda bg-sup-1 p-4">
          <h2 className="text-sm font-semibold">{t('crm.cfg.modelos.conteudo')}</h2>
          {(['apresentacao', 'condicoes', 'rodape'] as const).map((k) => (
            <Rotulo key={k} id={`${id}-${k}`} rotulo={t(`crm.proposta.campos.${k}`)}>
              <EditorRico id={`${id}-${k}`} rotulo={t(`crm.proposta.campos.${k}`)} dado={`modelo-${k}`} linhas={k === 'condicoes' ? 5 : 3} valor={m[k]} aoMudar={(v) => mudar({ [k]: v })} />
            </Rotulo>
          ))}
          <p className="text-xs font-semibold text-tinta-3 uppercase">{t('crm.proposta.previa')}</p>
          <DocumentoProposta p={{ cor: m.cor, titulo: m.titulo, ...blocosDaPrevia(m), itens: [], total_centavos: 0, moeda: 'BRL' }} cliente={null} contato={null} />
        </div>
      </form>
    </section>
  );
}
